import {BuildingIcon, EnvelopeIcon, LockIcon} from "assets/icons";
import useRecruiter from "contexts/User/useRecruiter";
import useAwaitableModal from "hooks/useAwaitableModal";
import {FC} from "react";
import {useNavigate} from "react-router";
import EditProfile from "./modals/EditProfile";
import EditPassword from "./modals/EditPassword";
import {maybeNotSetupRef} from "routes/private";
import EditAddress from "./modals/EditAddress";
import ChangeableProfilePicture from "components/ChangeableProfilePicture";

const formatAddress: (address?: any) => string = (address) => {
  if (!address) return "No Address Given";
  return [address.street1, address.street2, address.city, address.state, address.country].filter(Boolean).join(", ");
};

const ProfilePage: FC = () => {
  const user = useRecruiter();
  const navigate = useNavigate();
  const [editProfile, EditProfileModal] = useAwaitableModal(EditProfile, {} as any);
  const [editPassword, EditPasswordModal] = useAwaitableModal(EditPassword, {} as any);
  const [editAddress, EditAddressModal] = useAwaitableModal(EditAddress, {} as any);

  const onEdit = () => {
    editProfile({name: user.name, type: user.recruiterType})
      .then((edited) => {
        if (edited) {
          maybeNotSetupRef.current?.setUser?.({...user, name: edited.name, recruiterType: edited.type});
        }
      })
      .catch(() => {});
  };

  const onEditPassword = () => {
    editPassword({})
      .then(() => {})
      .catch(() => {});
  };

  const onEditAddress = () => {
    editAddress({...user.address})
      .then((address) => {
        if (address) {
          maybeNotSetupRef.current?.setUser?.({...user, address});
        }
      })
      .catch(() => {});
  };

  return (
    <div className="w-full h-full overflow-hidden flex flex-col bg-brand-surface">
      {EditProfileModal}
      {EditPasswordModal}
      {EditAddressModal}
      <div className="flex-1 overflow-y-auto p-5">
        <div className="max-w-2xl mx-auto flex flex-col gap-4">
          <h1 className="text-2xl font-bold text-brand-navy">User Profile</h1>
          <div className="bg-white rounded-2xl border border-brand-border overflow-hidden divide-y divide-brand-border">
            {/* Avatar + name */}
            <div className="flex items-center gap-5 p-5">
              <ChangeableProfilePicture size={80} src={user.image} />
              <div className="flex flex-col gap-0.5 grow min-w-0">
                <span className="text-lg font-semibold text-brand-navy truncate">{user.name}</span>
                <span className="text-sm text-brand-muted">{user.recruiterType}</span>
              </div>
              <button className="btn-secondary shrink-0" onClick={onEdit}>
                Edit Profile
              </button>
            </div>

            {/* Email */}
            <div className="flex items-center gap-4 px-5 py-4">
              <EnvelopeIcon className="w-5 h-5 text-brand-muted shrink-0" />
              <div className="flex flex-col grow min-w-0">
                <span className="text-xs text-brand-muted">Email Address</span>
                <span className="text-sm text-brand-navy">{user.email}</span>
              </div>
              <button className="btn-secondary shrink-0" onClick={() => navigate("/settings")}>
                Change Email
              </button>
            </div>

            {/* Password */}
            <div className="flex items-center gap-4 px-5 py-4">
              <LockIcon className="w-5 h-5 text-brand-muted shrink-0" />
              <div className="flex flex-col grow min-w-0">
                <span className="text-xs text-brand-muted">Password</span>
                <span className="text-sm text-brand-navy tracking-widest">••••••••••••</span>
              </div>
              <button className="btn-secondary shrink-0" onClick={onEditPassword}>
                Edit Password
              </button>
            </div>

            {/* Address */}
            <div className="flex items-center gap-4 px-5 py-4">
              <BuildingIcon className="w-5 h-5 text-brand-muted shrink-0" />
              <div className="flex flex-col grow min-w-0">
                <span className="text-xs text-brand-muted">Company Address</span>
                <span className="text-sm text-brand-navy">{formatAddress(user.address)}</span>
              </div>
              <button className="btn-secondary shrink-0" onClick={onEditAddress}>
                Edit Address
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
